import { filter, publishBehavior, refCount, shareReplay, startWith, map, find } from 'rxjs/operators';
import { from, interval, Observable } from 'rxjs';
import { createCancellationTokenSource } from './cancellation';

/**
 * Calls `factory` with a cancellation token to get an
 * observable input and subscribes to it, while
 * managing the cancellation token. Cancellations are not thrown as errors.
 */
export function withCancellation(factory) {
  return new Observable(function (subscriber) {
    var cancellation = createCancellationTokenSource();
    var subscription = from(factory(cancellation.token)).subscribe({
      complete: subscriber.complete.bind(subscriber),
      error: subscriber.error.bind(subscriber),
      next: function next(value) {
        subscriber.next(value);
      }
    });
    return function () {
      cancellation.cancel();
      cancellation = createCancellationTokenSource();
      subscription.unsubscribe();
    };
  });
}

/**
 * Returns a Cancellation Token that cancels either when the observable
 * emits a value that passes the specified predicate, or when it completes.
 *
 * If a predicate is not provided, it cancels when the first item emits.
 *
 * @param {Observable<T>} source$
 * @param {(item: T) => boolean} predicate
 */
export function cancelWhen(source$) {
  var predicate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var cts = createCancellationTokenSource();
  var sub = source$.pipe(find(predicate || function () {
    return true;
  })).subscribe({
    error: cts.cancel,
    complete: cts.cancel
  });
  cts.onDispose(function () {
    return sub.unsubscribe();
  });
  return cts;
}

/**
 * RxJS filter for filtering out nil values.
 */
export function filterNonNil() {
  return function (source$) {
    return source$.pipe(filter(function (value) {
      return value !== undefined && value !== null;
    }));
  };
}

/**
 * Returns an Observable that emits the initial or latest value on subscribe,
 * and also makes sure only a single subscription is made to the source.
 *
 * This means that **anything that goes before this operator** is triggered only once for
 * concurrent subscriptions.
 *
 * @param initialValue
 */
export function shareBehavior(initialValue) {
  return function (source$) {
    return source$.pipe(publishBehavior(initialValue), refCount());
  };
}

/**
 * Shortcut for shareReplay with refcounting and a buffer size of 1.
 */
export function shareLatest() {
  return function (source$) {
    return source$.pipe(shareReplay({
      refCount: true,
      bufferSize: 1
    }));
  };
}
export function fromClock() {
  var getNow = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : function () {
    return new Date();
  };
  return interval(1000).pipe(startWith(0), map(function () {
    return getNow();
  }));
}
//# sourceMappingURL=rxUtil.js.map
